import Promise from 'bluebird';
import { Storage } from 'enums';
import { compose } from 'lib/util';

export const OMNITURE_CONSTANTS = {
    Site: 'watchespn',
    ReportSuite: 'wdgesp360',
    TrackingServer: 'w88.espn.com'
};

const { Analytics: { IS_AUTHENTICATING } } = Storage;

// use a wrapper for localStorage so we can swap out the underlying mechanism if need be
const ls = {
    getItem: function(key) {
        const storedItem = localStorage.getItem(key);
        if (storedItem) {
            return JSON.parse(storedItem);
        }
        return false;
    },
    setItem: function(key, value) {
        localStorage.setItem(key, JSON.stringify(value));
    },
    removeItem: function(key) {
        localStorage.removeItem(key);
    }
};

let initialized = false;

const baseOmnitureData = {
    site: OMNITURE_CONSTANTS.Site,
    channel: 'web',
    account: OMNITURE_CONSTANTS.ReportSuite,
    premium: 'premium-no',
    prop1: 'watchespn'
};

function isCollectionsPage({ id: pageId = '', name: pageName = ''}) {
    return (pageId !== pageName.toLowerCase());
}

function getPageData(page) {
    const { name: pageName = '' } = page;
    const formattedPageName = pageName.toLowerCase();

    const omniturePageName = getOmniturePageName(page);

    // if collections page, add prop35
    const collectionsPageData = {};
    if (isCollectionsPage(page)) {
        collectionsPageData.prop35 = formattedPageName;
    }

    const pageData = {
        pageName: omniturePageName,
        sections: omniturePageName,
        section: omniturePageName,
        // only the schedule page has a different contentType... just do as we're told
        contentType: (formattedPageName === 'schedule') ? 'schedule' : 'index'
    };
    return Object.assign(pageData, collectionsPageData);
}

function getAuthenticationData(userAuth, provider) {
    const authenticated = (userAuth.indexOf('mvpd') !== -1 || userAuth.indexOf('isp') !== -1);
    const authenticationStatus = (authenticated === true ? 'authenticated' : 'not authenticated');
    let providerName = 'not authenticated';
    let providerAbbreviation = 'not authenticated';

    if (authenticated === true) {
        providerName = provider.name;
        providerAbbreviation = provider.abbreviation
    }

    const authnData = {
        eVar17: providerName,
        prop42: providerName,
        eVar32: providerAbbreviation,
        prop43: providerAbbreviation,
        eVar68: authenticationStatus
    };
    return authnData;
}

/**
  * This function calls the actual omniture page tracking
  */
function _trackPage(trackingData = {}) {
    if (espn && espn.track && Object.keys(trackingData).length > 0) {
        if (!initialized) {
            initialized = true;
            espn.track.init(trackingData);
        } else {
            espn.track.trackPage(trackingData && trackingData.omniture);
        }
    }
}

/**
  * This function calls the actual omniture link tracking
  */
function _trackLink(data = {}) {
    if (espn && espn.track) {
        espn.track.clear();
        const linkData = Object.assign(data, { link: 'true' });
        espn.track.userClicked = true;
        espn.track.trackLink(linkData);
    }
}

export function clearTrackingData() {
    ls.removeItem(IS_AUTHENTICATING);
}

export function getOmniturePageName(page) {
    const { id: pageId = '', name: pageName = '' } = page;
    const formattedPageName = pageName.toLowerCase();
    const formattedPageId = pageId.toLowerCase();
    return (!isCollectionsPage(page) ? formattedPageId : `${formattedPageId}:${formattedPageName}`);
}

export function trackMvpdPicker() {
    const site = baseOmnitureData.site;
    const mvpdPickerData = {
        events: 'event3,event49,event67',
        pageName: 'mvpd picker',
        eVar13: 'mvpd picker',
        prop4: 'authentication',
        prop5: 'authentication',
        eVar11: `authentication:${site}:authentication`,
    };
    const trackingData = {
        omniture: compose(
            baseOmnitureData,
            mvpdPickerData,
            getAuthenticationData([], {})
        )
    };
    _trackPage(trackingData);
}

export function trackProviderSelect({ userAuth, provider }) {
    const providerChosen = `provider chosen:${provider.name}`;
    const providerSelectData = {
        events: 'event72',
        pev2: provider.name,
        prop9: providerChosen,
        eVar74: providerChosen
    };
    const trackingData = compose(
        baseOmnitureData,
        getAuthenticationData(userAuth, provider),
        providerSelectData
    );
    _trackLink(trackingData);
    ls.setItem(IS_AUTHENTICATING, 'true');
}

export function trackAuthenticationComplete({ userAuth, provider }) {
    const isAuthenticating = ls.getItem(IS_AUTHENTICATING);
    if (isAuthenticating === 'true' || isAuthenticating === true) {
        const authenticationComplete = 'authentication complete';
        const authenticationCompleteData = {
            events: 'event68',
            pev2: authenticationComplete,
            prop9: authenticationComplete,
            eVar74: authenticationComplete
        };
        const trackingData = compose(
            baseOmnitureData,
            getAuthenticationData(userAuth, provider),
            authenticationCompleteData
        );
        _trackLink(trackingData);
        clearTrackingData();
    }
}

export function trackAuthorizationStart({ userAuth, provider }) {
    const authorizationStart = 'authorization start';
    const authorizationStartData = {
        events: 'event69',
        pev2: authorizationStart,
        prop9: authorizationStart,
        eVar74: authorizationStart
    };
    const trackingData = compose(
        baseOmnitureData,
        getAuthenticationData(userAuth, provider),
        authorizationStartData
    );
    setTimeout(() => {
        _trackLink(trackingData);
    }, 1000);
}

export function trackAuthorizationComplete({ userAuth, provider }) {
    const authorizationComplete = 'authorization complete';
    const authorizationCompleteData = {
        events: 'event70',
        pev2: authorizationComplete,
        prop9: authorizationComplete,
        eVar74: authorizationComplete
    };
    const trackingData = compose(
        baseOmnitureData,
        getAuthenticationData(userAuth, provider),
        authorizationCompleteData
    );
    setTimeout(() => {
        _trackLink(trackingData);
    }, 1000);
    
}

export function trackSearch({ fromHero, searchTerm }) {
    const context = (fromHero === true ? 'search-hero' : 'search-schedule');
    const searchTrackData = {
        events: 'event2',
        eVar4: `man:${searchTerm}`,
        eVar74: `${context}:submit`,
        prop9: `${context}:submit`
    };
    const trackingData = compose(
        baseOmnitureData,
        searchTrackData
    );
    _trackLink(trackingData);
}

export function trackUntimelyLogout(actual = 'unknown', expected = 'unknown', provider) {
    const untimelyLogout = 'Untimely Logout';

    const providerName = provider.name;
    const providerAbbreviation = provider.abbreviation

    const authnData = {
        eVar17: providerName,
        prop42: providerName,
        eVar32: providerAbbreviation,
        prop43: providerAbbreviation
    };

    const untimelyLogoutData = {
        events: 'event123',
        eVar37: navigator.userAgent,
        eVar74: untimelyLogout,
        eVar81: `actual:${actual}|expected:${expected}`,
        prop37: navigator.userAgent,
        prop9: untimelyLogout
    };
    const trackingData = compose(baseOmnitureData, authnData, untimelyLogoutData);
    _trackLink(trackingData);
}

export function trackLeavePageAlertShown() {
    const leavePageAlertShown = 'Leave Page Alert Shown';
    const leavePageData = {
        events: 'event120',
        eVar74: leavePageAlertShown,
        eVar72: leavePageAlertShown,
        prop9: leavePageAlertShown
    };
    const trackingData= compose(baseOmnitureData, leavePageData);
    _trackLink(trackingData);
}

export function trackReturnAfterLeavePageAlert() {
    const returnAfterLeavePageAlert = 'Return After Leave Page Alert';
    const leavePageData = {
        events: 'event121',
        eVar74: returnAfterLeavePageAlert,
        eVar72: returnAfterLeavePageAlert,
        prop9: returnAfterLeavePageAlert
    };
    const trackingData= compose(baseOmnitureData, leavePageData);
    _trackLink(trackingData);
}

export function trackPage({ app, page, provider, userAuth }) {
    const lang = (app.lang || '').toLowerCase();
    const pageTrackData = {
        events: 'event3',
        prop17: lang,
        eVar9: lang
    };

    const trackingData = {
        omniture: compose(
            baseOmnitureData,
            getPageData(page),
            getAuthenticationData(userAuth, provider),
            pageTrackData
        )
    };
    _trackPage(trackingData);
}



// WEBPACK FOOTER //
// ./app/lib/omniture/index.js